import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { StatCard } from "@/components/ui/stat-card";
import { PageHeader } from "@/components/ui/section-header";
import { LoadingPage } from "@/components/ui/loading-spinner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CareerTwinWidget from "../components/CareerTwinWidget";
import SkillDNAWidget from "../components/SkillDNAWidget";
import { 
  BookOpen, MessageSquare, Target, Trophy, Calendar, Award, Crown, Video, 
  TrendingUp, Users, GraduationCap, Sparkles, ArrowRight, CheckCircle2, User, Dna
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Dashboard = () => {
  const { user, token } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/progress/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { 
      icon: Target, 
      titleKey: "dashboard.takeAssessment", 
      descriptionKey: "dashboard.discoverStrengths", 
      path: "/assessment", 
      gradient: "from-primary to-primary/80",
      testId: "quick-action-assessment" 
    },
    { 
      icon: BookOpen, 
      titleKey: "dashboard.exploreCareers", 
      descriptionKey: "dashboard.browseCareerOptions", 
      path: "/careers", 
      gradient: "from-success to-success/80",
      testId: "quick-action-careers" 
    },
    { 
      icon: MessageSquare, 
      titleKey: "dashboard.aiCareerChat", 
      descriptionKey: "dashboard.askCareerQuestions", 
      path: "/chatbot", 
      gradient: "from-warning to-warning/80",
      testId: "quick-action-chatbot" 
    },
    { 
      icon: Video, 
      titleKey: "dashboard.bookConsultation", 
      descriptionKey: "dashboard.talkToExpert", 
      path: "/consultation/book", 
      gradient: "from-destructive to-destructive/80",
      testId: "quick-action-consultation" 
    },
  ];

  const exploreActions = [
    { icon: Dna, titleKey: "nav.skillDNA", title: "Your Skill DNA", path: "/skill-dna", testId: "explore-skill-dna" },
    { icon: Award, titleKey: "nav.scholarships", path: "/scholarships", testId: "explore-scholarships" },
    { icon: Trophy, titleKey: "nav.quizzes", path: "/quizzes", testId: "explore-quizzes" },
    { icon: Calendar, titleKey: "nav.events", path: "/events", testId: "explore-events" },
    { icon: BookOpen, titleKey: "nav.resources", path: "/resources", testId: "explore-resources" },
    { icon: Crown, titleKey: "dashboard.upgradePlan", path: "/subscriptions", testId: "explore-subscriptions" },
    { icon: Video, titleKey: "nav.myConsultations", path: "/consultations", testId: "explore-consultations" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <Navbar />
        <LoadingPage message="Loading your dashboard..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      
      <div className="container-custom py-6 md:py-10" data-testid="dashboard-container">
        {/* Welcome Section - Hero Style */}
        <div className="mb-10">
          <Card className="overflow-hidden border-none shadow-lg">
            <div className="relative bg-gradient-to-r from-primary via-primary/90 to-primary/80 p-8 md:p-10">
              {/* Decorative circles */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
              
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="inline-block px-4 py-1 bg-white/20 rounded-full text-white text-sm mb-4 backdrop-blur-sm">
                    {user?.role || 'Student'} • {t('dashboard.gradeLabel')} {user?.grade}
                  </div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" data-testid="dashboard-welcome">
                    {t('dashboard.welcomeBack')}, {user?.name}! 👋
                  </h1>
                  <p className="text-white/90 text-base md:text-lg mb-6">
                    Ready to take the next step in your career journey?
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-xl backdrop-blur-sm">
                      <Trophy className="w-5 h-5 text-white" />
                      <div>
                        <p className="text-white/80 text-xs">Points</p>
                        <p className="text-white font-bold text-lg">{user?.points || 0}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-xl backdrop-blur-sm">
                      <Award className="w-5 h-5 text-white" />
                      <div>
                        <p className="text-white/80 text-xs">Badges</p>
                        <p className="text-white font-bold text-lg">{user?.badges?.length || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="hidden lg:block">
                  <div className="relative w-40 h-40">
                    {user?.profile_image ? (
                      <div className="w-full h-full rounded-full overflow-hidden border-4 border-white/30 shadow-2xl">
                        <img 
                          src={user.profile_image} 
                          alt={user.name} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-white/20 rounded-full animate-pulse"></div>
                        <div className="absolute inset-2 bg-white/30 rounded-full flex items-center justify-center backdrop-blur-sm">
                          <User className="w-16 h-16 text-white" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Stats Cards - Larger with better spacing */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-primary/10 text-primary border-0">+12%</Badge>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Total Points</p>
              <h3 className="text-3xl font-bold text-foreground">{user?.points || 0}</h3>
              <p className="text-xs text-muted-foreground mt-2">{t('dashboard.keepEarningMore')}</p>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-warning to-warning/80 flex items-center justify-center shadow-md">
                  <Award className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Badges Earned</p>
              <h3 className="text-3xl font-bold text-foreground">{user?.badges?.length || 0}</h3>
              <p className="text-xs text-muted-foreground mt-2">{t('dashboard.collectThemAll')}</p>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-success to-success/80 flex items-center justify-center shadow-md">
                  <Target className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Assessments</p>
              <h3 className="text-3xl font-bold text-foreground">{dashboardData?.assessments_completed || 0}</h3>
              <p className="text-xs text-muted-foreground mt-2">Completed</p>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-destructive to-destructive/80 flex items-center justify-center shadow-md">
                  <Video className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-muted-foreground text-sm mb-1">Consultations</p>
              <h3 className="text-3xl font-bold text-foreground">{dashboardData?.consultations_booked || 0}</h3>
              <p className="text-xs text-muted-foreground mt-2">Booked sessions</p>
            </CardContent>
          </Card>
        </div>

        {/* Career Twin Widget */}
        <div className="mb-10">
          <CareerTwinWidget />
        </div>

        {/* Skill DNA Widget */}
        <div className="mb-10">
          <SkillDNAWidget />
        </div>

        {/* Career Readiness - Circular Progress */}
        {dashboardData?.career_readiness_score && (
          <Card className="mb-10 border-none shadow-lg">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                {/* Circular Progress */}
                <div className="relative w-48 h-48 flex-shrink-0">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      className="text-gray-200"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 88}`}
                      strokeDashoffset={`${2 * Math.PI * 88 * (1 - dashboardData.career_readiness_score / 100)}`}
                      className="text-primary transition-all duration-1000"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-5xl font-bold text-primary">
                      {Math.round(dashboardData.career_readiness_score)}%
                    </span>
                    <span className="text-xs text-muted-foreground">Complete</span>
                  </div>
                </div>
                
                {/* Progress Details */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <TrendingUp className="w-6 h-6 text-primary" />
                    <h3 className="text-2xl font-bold">Career Readiness Score</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Keep taking assessments and skill tests to improve your score!
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-success/10 text-success border-success/20">
                      Good Progress
                    </Badge>
                    <Badge variant="outline">On Track</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions - Larger Cards */}
        <div className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">{t('dashboard.quickActions')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Card
                  key={index}
                  className="border-none shadow-md hover:shadow-xl cursor-pointer group transition-all duration-300 overflow-hidden"
                  onClick={() => navigate(action.path)}
                  data-testid={action.testId}
                >
                  <CardContent className="p-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${action.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">
                      {t(action.titleKey)}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t(action.descriptionKey)}
                    </p>
                    <div className="flex items-center text-primary text-sm font-semibold group-hover:gap-2 transition-all">
                      <span>Get Started</span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Explore More Section */}
        <div className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">{t('dashboard.exploreMore')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {exploreActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Card
                  key={index}
                  className="border-none shadow-md hover:shadow-lg cursor-pointer group transition-all duration-300"
                  onClick={() => navigate(action.path)}
                  data-testid={action.testId}
                >
                  <CardContent className="p-5 text-center">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center mx-auto mb-3 group-hover:from-primary/20 group-hover:to-primary/10 transition-all">
                      <Icon className="w-7 h-7 text-primary group-hover:scale-110 transition-transform" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                      {action.title || t(action.titleKey)}
                    </h3>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Recommended Next Steps */}
        <Card className="border-none shadow-lg" data-testid="next-steps-section">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3 text-2xl">
              <CheckCircle2 className="w-6 h-6 text-primary" />
              {t('dashboard.recommendedNextSteps')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl border border-primary/20">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <h4 className="font-bold text-lg mb-2">Complete Your First Assessment</h4>
                <p className="text-sm text-muted-foreground mb-5 flex-1">
                  Take a quick assessment to discover careers that match your interests and skills.
                </p>
                <Button onClick={() => navigate("/assessment")} className="w-full" data-testid="next-step-assessment-btn">
                  Start Assessment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              
              <div className="flex flex-col p-6 bg-gradient-to-br from-success/5 to-success/10 rounded-2xl border border-success/20">
                <div className="w-14 h-14 rounded-xl bg-success/10 flex items-center justify-center mb-4">
                  <BookOpen className="w-7 h-7 text-success" />
                </div>
                <h4 className="font-bold text-lg mb-2">Explore Scholarships & Exams</h4>
                <p className="text-sm text-muted-foreground mb-5 flex-1">
                  Find scholarships you're eligible for and prepare for entrance exams.
                </p>
                <Button variant="outline" onClick={() => navigate("/scholarships")} className="w-full border-success/20 hover:bg-success/10" data-testid="next-step-scholarships-btn">
                  View Scholarships
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;